/* facts-typewriter.js · FACTS · Schreibmaschine in der Headline
 * ------------------------------------------------------------------
 * <facts-typewriter> tippt ein Wort Zeichen fuer Zeichen, haelt es,
 * loescht es und tippt das naechste. Der Punkt davor ist der Cursor:
 * er blinkt langsam, solange nichts passiert, und steht still, waehrend
 * getippt wird - so wie an einer echten Maschine.
 *
 * Regeln, an die sich das Skript haelt:
 *  - Das ERSTE Wort steht bereits im HTML. Ohne JS, mit reduzierter
 *    Bewegung und fuer jede Maschine, die den Text liest, ist die
 *    Headline damit vollstaendig. Das Skript uebernimmt erst danach.
 *  - prefers-reduced-motion: reduce -> gar keine Bewegung. Das erste
 *    Wort bleibt stehen, der Punkt hoert auf zu blinken (CSS).
 *  - Ausserhalb des Bildschirms und im Hintergrund-Tab laeuft nichts.
 *  - Das erste Wort wird IMMER getippt, auch wenn es das einzige ist: das
 *    Feld wird geleert, der Punkt blinkt allein, dann schreibt sich das
 *    Wort.
 *  - Danach laeuft die Liste weiter: halten, loeschen, naechstes Wort. Bei
 *    genau EINEM Wort wird dasselbe Wort geloescht und neu getippt - das ist
 *    ausdruecklich gewollt, die Zeile soll in Bewegung bleiben.
 *    (Julian 2026-08-27)
 *  - Endlos ist der Standard, laesst sich aber auf eine Zahl von Durchlaeufen
 *    begrenzen. Danach bleibt das letzte Wort stehen und nur der Punkt
 *    blinkt weiter.
 *  - setTimeout, nicht requestAnimationFrame: hier wechselt alle 40 bis
 *    70 Millisekunden ein Textknoten, kein Bild pro Bildwiederholung.
 *
 * Die Breite des Wortfeldes haelt das CSS konstant (unsichtbare Kopien
 * aller Woerter im selben Rasterfeld). Deshalb springt die Zeile beim
 * Wortwechsel nicht - hier im Skript ist dazu nichts zu tun.
 */
(function () {
  if (customElements.get('facts-typewriter')) return;

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class FactsTypewriter extends HTMLElement {
    connectedCallback() {
      if (this._on) return;
      this._on = true;

      this._live = this.querySelector('[data-tw-live]');
      if (!this._live) return;

      var raw = this.getAttribute('data-words') || '';
      this._words = raw.split('|').map(function (w) { return w.trim(); }).filter(Boolean);
      if (this._words.length === 0) return;

      this._type  = num(this, 'data-type', 70);
      this._erase = num(this, 'data-erase', 38);
      this._hold  = num(this, 'data-hold', 1900);
      this._gap   = num(this, 'data-gap', 320);
      this._start = num(this, 'data-start', 600);
      this._loop  = this.getAttribute('data-loop') !== '0';
      this._runs  = num(this, 'data-runs', 3);
      this._done  = false;
      this._cycle = 0;

      /* Reduzierte Bewegung: das erste Wort steht schon da, fertig. */
      if (REDUCE) return;

      this._i = 0;
      this._n = 0;
      this._t = null;
      this._visible = false;

      this._onVis = this._onVis.bind(this);
      document.addEventListener('visibilitychange', this._onVis);

      var self = this;
      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            self._visible = e.isIntersecting;
            if (self._visible) self._resume(); else self._pause();
          });
        }, { rootMargin: '0px 0px -10% 0px' });
        this._io.observe(this);
      } else {
        this._visible = true;
        this._resume();
      }
    }

    disconnectedCallback() {
      this._pause();
      document.removeEventListener('visibilitychange', this._onVis);
      if (this._io) { this._io.disconnect(); this._io = null; }
      this._on = false;
    }

    _onVis() {
      if (document.hidden) this._pause(); else if (this._visible) this._resume();
    }

    _pause() {
      if (this._t) { clearTimeout(this._t); this._t = null; }
      this.classList.remove('is-typing');
    }

    _resume() {
      if (this._t || document.hidden || !this._visible) return;
      if (this._done) return;
      if (!this._began) {
        this._began = true;
        this._i = 0;
        this._n = 0;
        /* Das Feld wird geleert und das erste Wort GETIPPT - auch wenn es das
           einzige ist. Vorher blieb es stehen, weil Loeschen und Neutippen
           desselben Wortes sinnlos schien; gemeint war aber genau das:
           erst blinkt der Punkt allein, dann schreibt sich das Wort.
           (Julian 2026-08-27) */
        this._write('');
        this._t = setTimeout(this._tick.bind(this), this._start);
        return;
      }
      this._t = setTimeout(this._tick.bind(this), this._type);
    }

    _tick() {
      this._t = null;
      var word = this._words[this._i];

      if (this._erasing) {
        this._n -= 1;
        this._write(word.slice(0, this._n));
        if (this._n <= 0) {
          this._erasing = false;
          this._i = (this._i + 1) % this._words.length;
          if (this._i === 0) { this._cycle += 1; }
          this._schedule(this._gap, false);
        } else {
          this._schedule(this._erase, true);
        }
        return;
      }

      if (this._n < word.length) {
        this._n += 1;
        this._write(word.slice(0, this._n));
        this._schedule(this._type, true);
        return;
      }

      /* Wort steht. Halten, dann loeschen - auch wenn es das einzige Wort ist.
         Ein Durchlauf ist eine volle Runde durch die Liste. */
      if (!this._loop && this._cycle >= this._runs) {
        this.classList.remove('is-typing');
        this._done = true;
        return;
      }
      this._erasing = true;
      this._schedule(this._hold, false);
    }

    _schedule(ms, typing) {
      this.classList.toggle('is-typing', !!typing);
      if (document.hidden || !this._visible) { this.classList.remove('is-typing'); return; }
      this._t = setTimeout(this._tick.bind(this), ms);
    }

    _write(text) {
      this._live.textContent = text;
    }
  }

  function num(el, attr, fallback) {
    var v = parseInt(el.getAttribute(attr), 10);
    return isNaN(v) ? fallback : v;
  }

  customElements.define('facts-typewriter', FactsTypewriter);
})();
